<template>
  <div data-component-group="contract-form" data-component="cap-amount">
    <slot name="beforeForm" />

    <form novalidate @submit.prevent="addCapAmount">
      <magic-container>
        <v-text-field
          v-model="cappingAmount"
          :label="$t('capping_amount')"
          required
          type="number"
          :prefix="currencySymbol"
          :readonly="isDisabled"
          :error-messages="capAmountErrors"
          @blur="v$.cappingAmount.$touch()"
        />

        <month-select
          :date="expireAt"
          :placeholder="$t('expire_at_optional')"
          :hint="$t('capping_amount_expire_date_hint')"
          :persistent-hint="true"
          :hide-details="false"
          :min-date="minDate()"
          :max-date="maxDate()"
          :is-disabled="isDisabled"
          :on-change="(date) => expireAt = date"
          :on-clear="() => expireAt = ''"
        />
      </magic-container>

      <div v-if="!isDisabled" class="form-actions mt-2">
        <v-btn
          color="secondary"
          class="trigger"
          :loading="isLoading"
          type="submit"
        >
          <v-icon>{{ triggerIcon }}</v-icon>
          {{ triggerLabel }}
        </v-btn>
      </div>
    </form>

    <Removables
      v-if="hasCapAmount"
      :items="capAmountArr"
      :on-remove="removeCapAmount"
      :is-disabled="isDisabled"
    >
      <template v-slot:item="scope" >
        <AmountLabel
          :label="$t('capping_amount')"
          size="lg"
          :value="getFormattedCappingAmount(scope.item?.amount)"
        />
        <AmountLabel
          :label="$t('expire_at')"
          :value="getExpireDate(scope.item?.expire_at)"
        />
      </template>
    </Removables>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import { isObj, isStr } from '../../../utils';
  import AmountLabel from '../../labels/Amount';
  import Removables from '../../lists/Removables';

  export default {
    components: {
      AmountLabel,
      Removables,
    },
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        cappingAmount: {
          required,
        },
      };
    },
    props: {
      contract: {
        type: Object,
        required: true,
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        cappingAmount: '',
        expireAt: '',
        isLoading: false,
      };
    },
    computed: {
      activeContract() {
        return this.$store.getters['contracts/activeItem'];
      },
      capAmountArr(){
        return [this.activeContract.capping_discount];
      },
      hasCapAmount() {
        return isObj(this.activeContract.capping_discount);
      },
      showExpireDateHint() {
        return true;
      },
      currencySymbol() {
        const { contract } = this;
        return isObj(contract) && isObj(contract.country.currency)
          ? contract.country.currency.symbol
          : '';
      },
      capAmountErrors() {
        return this.v$.cappingAmount.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('capping_amount_required');
          }
          return error.$message;
        });
      },
      triggerIcon() {
        const isCapAmountAlreadySet = this.isThereExistingCapAmount();

        return isCapAmountAlreadySet ? 'edit' : 'add'
      },
      triggerLabel() {
        const isCapAmountAlreadySet = this.isThereExistingCapAmount();

        return isCapAmountAlreadySet ? this.$t('update') : this.$t('add');
      },
    },
    watch: {
      contract(newContract, oldContract) {
        if (newContract.id !== oldContract.id) {
          this.clear();
        } else {
          this.v$.$reset();
        }
      },
      isDisabled(newValue) {
        if (newValue) {
          this.clear();
        }
      },
    },
    methods: {
      minDate() {
        return this.$dayjs().format('YYYY-MM');
      },
      maxDate() {
        return this.activeContract?.expire_at;
      },
      onChangeFrom(expireAt) {
        this.expireAt = expireAt;

        if (this.capAmountArr.length) {
          const selectedItem = this.capAmountArr.filter((p) => p?.expireAt === expireAt);

          if (isObj(selectedItem[0])) {
            this.caAmountArr = selectedItem[0].caAmountArr;
            return;
          }

          this.caAmountArr = '';
        }
      },
      removeCapAmount() {
        return this.$store
          .dispatch('contracts/removeCapAmount', this.contract.id)
          .then((res) => {
            this.clear();
            return res;
          });
      },
      getFormattedCappingAmount(cappingAmount) {
        const { currency } = this.contract.country;

        return isObj(currency) && isStr(currency.symbol)
          ? `${cappingAmount} ${currency.symbol}`
          : cappingAmount;
      },
      getExpireDate(expireDate) {
        let date = 'N/A'
        if (expireDate) {
          date = expireDate;
        }
        if (!expireDate && this.activeContract.expire_at) {
          date = this.activeContract.expire_at;
        }
        return date;
      },
      addCapAmount() {
        if (this.isLoading) return;

        this.v$.$touch();

        const { contract, cappingAmount, expireAt } = this;
        const query = {
          id: contract.id,
          amount: cappingAmount,
          expire_at: expireAt
        };

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        this.isLoading = true;
        return this.$store
          .dispatch('contracts/addCapAmount', query)
          .then((res) => {
            this.clear();
            return res;
          })
          .catch(err => {
            return err;
          })
          .finally(() => {
            this.isLoading = false;
          });
      },
      canSubmitRequest() {
        return this.cappingAmount !== ''
      },
      isThereExistingCapAmount() {
        return this.activeContract.capping_discount;
      },
      clear() {
        this.v$.$reset();
        this.cappingAmount = '';
        this.expireAt = '';
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
  @use '../../../assets/styles/theme' as *;

  [data-component-group='contract-form'][data-component='cap-amount'] {
    padding: $spacing-xl;
    background-color: rgb(var(--v-theme-surface));
    border-top: 1px solid map.get($colors, grey, lighten-2);

    .v-list {
      background: transparent;
    }

    .price-item {
      margin: 0 0 10px 0;
      border-radius: $border-radius;
      background-color: rgb(var(--v-theme-surface));
    }

  // TODO replace with form-buttons
  .form-actions {
      text-align: right;

      .v-btn {
        margin: 0;

        .v-icon {
          margin: 0 6px 0 0;
        }
      }
    }

    [data-component="date-picker"] {
      .v-messages__message {
        margin-bottom: 15px;
        padding-right: 20px;
        line-height: 16px;
      }
    }

    [data-component='amount'] {
      .item-label {
        min-width: 100px;
      }
    }

    [data-component='removables'] {
      padding: 16px 0 0 0;

      .v-list {
        padding: 0;
      }

      .v-list-item {
        height: 62px;
        border-radius: $border-radius;
        background-color: rgba(var(--v-theme-on-surface), 0.06);
      }
    }
  }
</style>
